/**
 * Centralised mapping of route names → required permission(s).
 *
 * The router guard (middleware/auth.js) reads this map to decide whether to
 * allow navigation. The sidebar menu reads it to hide entries the user cannot
 * access. Permission strings EXACTLY match those seeded in RoleSeeder.php
 * (verified against live DB — 291 permissions).
 *
 * - A string        => user must have that single permission.
 * - An array        => user must have ANY one of the listed permissions.
 * - Route not listed => no permission check (only authentication).
 *
 * Super Admin / Admin pass permission checks, while explicit route role gates
 * (such as the Super-Admin-only Settings area) are still enforced.
 */
export const routePermissions = {
  // ── Audit & versioning ───────────────────────────────────────────────────
  "audit-log": "audit.read",
  "annual-plan-versions": "annual-business-plans.read",

  // ── User Management ──────────────────────────────────────────────────────
  "user-list": "users.read",
  "user-create": "users.create", // /profile/create or any user-create route

  // ── Settings ─────────────────────────────────────────────────────────────
  // Settings pages are restricted to Super Admin only.
  // The sidebar gate (requiresRole) hides the menu; these route entries block direct URL access.
  roles: "roles.read",
  permissions: "permissions.read",
  departments: "departments.read", // Business Admin excluded (no departments.read)
  divisions: "divisions.read",

  // ── PMO Dashboard ─────────────────────────────────────────────────────────
  "pmo-dashboard": [
    "pmo.dashboard",
    "pmo.project-tracker",
    "pmo.program-tracker",
    "pmo.council-board",
    "strategic.plans.read",
  ],

  // ── Projects ─────────────────────────────────────────────────────────────
  "project-home": "dashboard.access",
  "project-dashboard": "dashboard.access",
  projects: "projects.read",
  "project-create": "projects.create",
  "project-detail": ["projects.read", "projects.details.read"],
  "project-review": [
    "projects.approval.manager.approve",
    "projects.approval.planning.approve",
    "projects.approval.pmo.approve",
    "projects.approval.board.approve",
  ],
  // ── Programs ─────────────────────────────────────────────────────────────
  // Programs and Portfolios are PMO-only modules; the API enforces the same.
  portfolios: "portfolios.read",
  "portfolio-create": "portfolios.create",
  "portfolio-detail": "portfolios.read",
  "portfolio-review": "portfolios.read",
  programs: "programs.read",
  "program-create": ["programs.manage", "programs.read"],
  "program-detail": "programs.read",
  "program-review": "programs.approve",

  // ── Strategic Plans ───────────────────────────────────────────────────────
  "smo-dashboard": "strategic.plans.read",
  // 'strategic-house':                  'strategic.plans.read',
  "strategic-plans": "strategic.plans.read",
  "initiative-views": "strategic.initiatives.read",
  // Everyone who reads OPIs may see the thresholds; only SMO/admins can change them (page + API).
  // OPIs are the Annual Business Plan module's own indicators and carry their own
  // permission family, so a Division Head or OPI Champion reaches them without any
  // Strategic KPI grant.
  "opi-settings": "strategic.opi.read",
  "opi-detail": "strategic.opi.read",
  "annual-business-plan": "annual-business-plans.read",
  "annual-business-plan-list": "annual-business-plans.read",
  "department-assignment": "annual-business-plans.manage",
  "annual-business-plan-create": "annual-business-plans.manage",
  // The page itself checks the plan's read-only/review abilities.
  "annual-business-plan-edit": [
    "annual-business-plans.manage",
    "annual-business-plans.approve",
  ],
  "annual-business-plan-detail": "annual-business-plans.read",
  "istedama-library": "library.read",
  "master-plan": "annual-business-plans.read",
  "master-plan-detail": "annual-business-plans.read",
  "plan-detail": "annual-business-plans.read",
  "plan-management": "annual-business-plans.read",
  // PMO adds details; the VP reworks CEO rejections (the page checks the plan's abilities).
  "master-plan-project-card": ["master-plans.manage", "annual-business-plans.approve"],
  "master-plan-edit": ["master-plans.manage", "annual-business-plans.approve"],
  "strategic-plan-create": ["strategic.plans.create", "strategic.kpi.create"],
  "strategic-plan-edit": ["strategic.plans.update", "strategic.kpi.update"],
  "strategic-plan-detail": "strategic.plans.read",

  // ── Strategic KPIs ────────────────────────────────────────────────────────
  "strategic-kpis": "strategic.kpi.read",
  "strategic-kpi-create": "strategic.kpi.create",
  "strategic-kpi-edit": "strategic.kpi.update",
  "strategic-kpi-detail": "strategic.kpi.read",
  "strategic-kpi-review": "strategic.kpi.approve",

  // ── KPI Change Requests ───────────────────────────────────────────────────
  "kpi-change-requests": "strategic.kpi.changes.read",
  "kpi-change-request-create": "strategic.kpi.changes.create",
  "kpi-change-request-edit": "strategic.kpi.changes.create",
  "kpi-change-request-detail": "strategic.kpi.changes.read",

  // ── KPI Measurements ──────────────────────────────────────────────────────
  "kpi-measurements": "strategic.kpi.measurements.read",
  // Now only a shim that opens the measurement dialog for links already sent out in
  // notifications; what may be edited is decided per period inside the dialog.
  "kpi-measurement-update": "strategic.kpi.measurements.read",
  "kpi-measurement-review": "strategic.kpi.measurements.approve",

  // ── Initiatives ───────────────────────────────────────────────────────────
  initiatives: "strategic.initiatives.read",
  // Strategy-side page: the SMO reaches it through the initiative, not through projects.*
  "initiative-projects": ["strategic.initiatives.read", "projects.read"],
  "initiatives-roadmap": "strategic.initiatives.read",
  "initiative-create": "strategic.initiatives.create",
  "initiative-edit": "strategic.initiatives.update",
  "initiative-detail": "strategic.initiatives.read",
  // The KPIs / Projects / Change Requests workspace for one initiative.
  "initiative-manage": "strategic.initiatives.read",
  "initiative-review": "strategic.initiatives.approve",
  "initiative-update": "strategic.initiatives.execution.update",
  "initiative-review-update": "strategic.initiatives.approve",
  "initiative-review-closure": "strategic.initiatives.closure.approve",
  "initiative-closure": "strategic.initiatives.closure.submit",
  "initiatives-update-list": "strategic.initiatives.execution.read",
  "initiatives-closure-list": "strategic.initiatives.closure.submit",

  // ── Corrective Action Plans ───────────────────────────────────────────────
  "corrective-plans": "strategic.kpi.corrective-actions.read",
  "corrective-plan-create": "strategic.kpi.corrective-actions.create",
  "corrective-plan-edit": "strategic.kpi.corrective-actions.update",
  "corrective-plan-detail": "strategic.kpi.corrective-actions.read",
  "corrective-plan-review": "strategic.kpi.corrective-actions.read",

  // ── Strategic Risks ───────────────────────────────────────────────────────
  "strategy-risks": "strategic.risks.read",
  "strategy-risk-create": "strategic.risks.create",
  "strategy-risk-edit": "strategic.risks.update",
  "strategy-risk-detail": "strategic.risks.read",
  "strategy-risk-review": "strategic.risks.read",

  // ── Strategic Challenges ─────────────────────────────────────────────────
  "strategy-challenges": "strategic.challenges.read",
  "strategy-challenge-create": "strategic.challenges.create",
  "strategy-challenge-edit": "strategic.challenges.update",
  "strategy-challenge-detail": "strategic.challenges.read",
  "strategy-challenge-review": "strategic.challenges.read",

  // ── Initiative Change Requests ────────────────────────────────────────────
  "initiative-change-requests": "strategic.initiatives.changes.read",
  "initiative-change-request-create": "strategic.initiatives.changes.create",
  "initiative-change-request-edit": "strategic.initiatives.changes.create",
  "initiative-change-request-detail": "strategic.initiatives.changes.read",
  "initiative-change-request-review": "strategic.initiatives.changes.approve",
};

const PROJECT_ROLES = [
  "Project Manager",
  "Project Owner",
  "Project Sponsor",
  "Project Council",
  "Project Board",
];

const PROGRAM_ROLES = [
  "Program Manager",
  "Program Council",
  "Program Council · Chairperson Role",
  "Program Council · Secretary Role",
  "Program Owner",
  "Program Director",
  "Program Sponsor",
];

const STRATEGY_ROLES = [
  "SMO",
  "SMO Director",
  "Strategy Manager",
  "Strategy Specialist",
];

const MASTER_PLAN_ROLES = ["PMO", "Project Owner", "VP", "CEO"];

/**
 * Roles that may not open a route even when they hold its permission.
 *
 * A Project Manager holds annual-business-plans.read so they can read the plan their own work
 * came from, which would otherwise let them open the Master Plan register by typing its URL.
 * The register is the PMO's and the Project Owner's workspace; a manager works from their own
 * project list instead.
 */
export const routeBlockedRoles = {
  "master-plan": ["Project Manager"],
  "master-plan-project-card": ["Project Manager"],
};

export const routePermissionRoleFallbacks = {
  "pmo-dashboard": ["PMO", ...PROJECT_ROLES, ...PROGRAM_ROLES, ...STRATEGY_ROLES],
  "smo-dashboard": STRATEGY_ROLES,
  // 'strategic-house': STRATEGY_ROLES,
  // 'strategic-plans': STRATEGY_ROLES,
  "initiatives-roadmap": STRATEGY_ROLES,
  // PMO manages plan details, the Project Owner assigns approved plans, and VP/CEO review
  // the plan, so these roles can open the Master Plan register.
  "master-plan": MASTER_PLAN_ROLES,
  "master-plan-detail": MASTER_PLAN_ROLES,
  "plan-detail": [...MASTER_PLAN_ROLES, "Project Manager"],
  "plan-management": [
    ...MASTER_PLAN_ROLES,
    "Project Manager",
    "Risk Section Director",
    "Project Sponsor",
    "Sponsor",
  ],
};

/**
 * Convenience: derive required permission for a menu item that only knows
 * its URL path (not the Vue route name). Used by sidebar menu.js.
 */
export function getRequiredPermissionFor({ name, path } = {}) {
  if (name && routePermissions[name] !== undefined)
    return routePermissions[name];
  if (!path) return null;

  const pathToName = {
    "/profile/list": "user-list",
    "/roles": "roles",
    "/permissions": "permissions",
    "/departments": "departments",
    "/divisions": "divisions",
    "/project/pmo-dashboard": "pmo-dashboard",
    "/project/home": "project-home",
    "/project/dashboard": "project-dashboard",
    "/project/projects": "projects",
    "/project/programs": "programs",
    "/portfolio": "portfolios",
    // '/strategy/smo-dashboard':              'smo-dashboard',
    // '/strategic-house':                     'strategic-house',
    "/strategy/audit-log": "audit-log",
    "/strategy/strategic-plans": "strategic-plans",
    "/strategy/annual-business-plan": "annual-business-plan",
    "/strategy/annual-business-plan/list": "annual-business-plan-list",
    "/strategy/annual-business-plan/department-assignment":
      "department-assignment",
    "/library": "istedama-library",
    "/master-plan": "master-plan",
    "/plan-management": "plan-management",
    "/project-management": "plan-management",
    "/strategy/strategic-kpis": "strategic-kpis",
    "/strategy/initiatives": "initiatives",
    "/initiatives-roadmap": "initiatives-roadmap",
    "/strategy/kpi-change-requests": "kpi-change-requests",
    "/strategy/initiative-change-requests": "initiative-change-requests",
    "/strategy/corrective-plans": "corrective-plans",
    "/strategy/risks": "strategy-risks",
    "/strategy/challenges": "strategy-challenges",
  };
  const matchedName = pathToName[path];
  return matchedName ? (routePermissions[matchedName] ?? null) : null;
}
